import {
    Box,
    Checkbox,
    Divider,
    Flex,
    FormControl,
    FormErrorMessage,
    FormLabel,
    HStack,
    Image,
    Input,
    InputGroup,
    InputRightElement,
    Radio,
    RadioGroup,
    Show,
    Skeleton,
    Stack,
    Text,
    Textarea,
    useMediaQuery,
    VStack,
} from '@chakra-ui/react'
import { useSendSmsMutation } from '@core/app/api/authApi'
import { useGetPostInfoQuery, useSetJobApplyCandidateMutation } from '@core/app/api/postsApi'
import { ApplyJobSettings, ScreeningQuestion } from '@core/app/types/postsApiType'
import { AppButton } from '@core/components/AppButton'
import { FieldError } from '@core/components/FieldError/FieldError'
import { FlagSelect } from '@core/components/FlagSelect/FlagSelect'
import { AutoResizeTextarea } from '@core/components/ResizeTextarea/ResizeTextarea'
import { DefaultSelect } from '@core/components/Select/Select'
import { SelectGoogleAutocomplete } from '@core/components/SelectGoogleAutocomplete/SelectGoogleAutocomplete'
import { RECAPTCHA } from '@core/utils/constants/constants'
import { useIsApplyDialog } from '@core/utils/contexts/ApplyDialogContext/ApplDialogContext'
import decodeHtmlEntitiesRecursive from '@core/utils/helpers/decodeHtmlEntitiesRecursive'
import { getDaysArray } from '@core/utils/helpers/getDaysArray'
import { getYearsArray } from '@core/utils/helpers/getYearsArray'
import { getLocalStorageItem } from '@core/utils/helpers/localStorageHelpers'
import useIsEmployee from '@core/utils/hooks/useIsEmployee'
import { Form, Formik } from 'formik'
import checked from '@page/settings/assets/images/checked_solid.svg'
import { useEffect, useMemo, useRef, useState } from 'react'
import ReCAPTCHA from 'react-google-recaptcha'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import * as Yup from 'yup'
import { ApplyJobDropzone } from '@core/components/ApplyJobDropzone'

interface Props {
    settings: ApplyJobSettings | undefined
    isSettingsLoading: boolean
    isSettingsFetching: boolean
}

interface IAttachment {
    name: string
    ext: string
    base64: string
}

interface DateOfBirth {
    day: {
        value: string
        label: string
    }
    month: {
        value: string
        label: string
    }
    year: {
        value: string
        label: string
    }
}

type GenderString = '0' | '1' | '2' | '3' | '4'

type MobilePhone = {
    prefix: string
    number: string
}

type Address = {
    label: string
    address1: string
    city: string
    country: string
    postalCode: string
}

interface FormValues {
    applicationText: string
    terms: boolean
    anonymous: boolean
    reasonText?: string
    address: Address
    attachments: IAttachment[]
    cv: IAttachment | null
    dateOfBirth: DateOfBirth
    gender: GenderString
    linkedinUrl: string | null
    mobilePhone: MobilePhone
}

interface CustomError extends Error {
    data?: {
        data?: {
            errors?: {
                email?: string[]
                mobilePhone?: {
                    number?: string[]
                    prefix?: string[]
                }
                dateOfBirth?: string[]
                address?: {
                    address1?: string[]
                    city?: string[]
                    country?: string[]
                    postalCode?: string[]
                }
                validateMobileId?: string[]
                attachments?: any
                cv?: {
                    base64?: string[]
                    ext?: string[]
                    name?: string[]
                }
                screeningQuestions?: string[]
                applicationText?: string[]
                firstName?: string[]
                lastName?: string[]
                anonymousReason?: string[]
                anonymousApplication?: string[]
                gender?: string[]
                linkedinUrl?: string[]
            }
        }
    }
}

const formatDateOfBirth = (dob: DateOfBirth | null) => {
    if (!dob) return null
    if (!dob.day?.value.length && !dob.month?.value.length && !dob?.year?.value.length) {
        return null
    }
    return `${dob.year.value}-${dob.month.value}-${dob.day.value}`
}

function getAddress(addr: Address | null) {
    if (!addr) return null
    if (addr && (addr.city.length === 0 || addr.city.length === 0)) {
        return null
    }
    return addr
}

function getGenderNumber(gen: GenderString | null): 0 | 1 | 2 | 3 | 4 | null {
    if (gen === '1' || gen === '2' || gen === '3' || gen === '4') {
        return parseInt(gen, 10) as 0 | 1 | 2 | 3 | 4
    }
    return null
}

function getMobilePhone(mobilePhone: MobilePhone | null) {
    if (!mobilePhone) return null
    if (mobilePhone && (mobilePhone.number.length === 0 || mobilePhone.prefix.length === 0)) {
        return null
    }
    return mobilePhone
}

const cvSchema = Yup.object().nullable()

export const ApplicationText = ({ settings, isSettingsLoading }: Props) => {
    const [applyJob, { isLoading }] = useSetJobApplyCandidateMutation()
    const { id } = useParams()
    const [screeningQuestions, setScreeningQuestions] = useState<ScreeningQuestion[]>([])
    const [symbolsCount, setSymbolsCount] = useState<number>(0)
    const recaptchaRef = useRef<ReCAPTCHA>(null)
    const { t } = useTranslation()
    const { close } = useIsApplyDialog()
    const [questionsError, setQuestionsError] = useState<boolean>(false)
    const [selectedParseValue, setSelectedParseValue] = useState('yes')

    const [attachments, setAttachments] = useState<IAttachment[]>([])
    const [showValidationModal, setShowValidationModal] = useState<boolean>()
    const [mobilePhone, setMobilePhone] = useState<MobilePhone>()
    const phoneInputRef = useRef<HTMLInputElement | null>(null)
    const [isDesktop] = useMediaQuery('(min-width: 1230px)', {
        fallback: false,
    })
    const [isMobileValid, setIsMobileValid] = useState<boolean>(false)
    const [sendSMS] = useSendSmsMutation()
    const { isEmployee } = useIsEmployee()
    const [attachmentsError, setAttachmentsError] = useState<IAttachment[]>([])
    const postId = location.pathname.includes('/job/') ? id : getLocalStorageItem('apply_dialog')?.id
    const postNumberId = useMemo(() => {
        const n = Number(postId)
        return isNaN(n) ? 0 : n
    }, [postId])
    const { data: postInfo } = useGetPostInfoQuery(
        {
            id: postNumberId,
        },
        { skip: !postNumberId }
    )
    const genders = [
        {
            value: '1',
            label: t('68', 'Male'),
        },
        {
            value: '2',
            label: t('69', 'Female'),
        },
        {
            value: '3',
            label: t('1144', 'Other'),
        },
        {
            value: '4',
            label: t('8888', 'Prefer not to say'),
        },
    ]
    const days = getDaysArray()
    const years = getYearsArray()
    const Months = [
        { value: '01', label: `${t('477')}` },
        { value: '02', label: `${t('478')}` },
        { value: '03', label: `${t('479')}` },
        { value: '04', label: `${t('480')}` },
        { value: '05', label: `${t('481')}` },
        { value: '06', label: `${t('482')}` },
        { value: '07', label: `${t('483')}` },
        { value: '08', label: `${t('484')}` },
        { value: '09', label: `${t('485')}` },
        { value: '10', label: `${t('486')}` },
        { value: '11', label: `${t('487')}` },
        { value: '12', label: `${t('488')}` },
    ]
    const getLocalStorageMobileVerification = () => {
        const mobileVerification = localStorage.getItem('mobile_verification')
        let prefix = ''
        let number = ''
        if (mobileVerification) {
            try {
                const parsedMobileVerification = JSON.parse(mobileVerification).number
                prefix = parsedMobileVerification.prefix
                number = parsedMobileVerification.number
            } catch (e) {
                console.warn(e)
            }
        }
        return { prefix, number }
    }
    const initialValues: FormValues = {
        applicationText: '',
        terms: false,
        anonymous: false,
        reasonText: '',
        attachments: [],
        mobilePhone: getLocalStorageMobileVerification(),
        address: {
            label: '',
            address1: '',
            country: '',
            city: '',
            postalCode: '',
        },
        cv: null,
        dateOfBirth: {
            day: {
                value: '',
                label: '',
            },
            month: {
                value: '',
                label: '',
            },
            year: {
                value: '',
                label: '',
            },
        },
        gender: '0',
        linkedinUrl: null,
    }

    const sendValidationSMS = async (prefix: string, number: string) => {
        recaptchaRef.current?.reset()
        let token = await recaptchaRef.current?.executeAsync()

        if (token) {
            try {
                await sendSMS({
                    data: {
                        'g-recaptcha-response': token,
                        mobilePhone: {
                            prefix,
                            number: number.toString(),
                        },
                    },
                }).unwrap()

                setShowValidationModal(true)
                setMobilePhone({
                    prefix,
                    number: number.toString(),
                })
            } catch (error) {}
        }
    }

    const submitHandler = async (
        values: FormValues,
        screeningQuestions: any,
        setSubmitting: Function,
        setError: Function
    ) => {
        recaptchaRef.current?.reset()
        let token = await recaptchaRef.current?.executeAsync()
        if (postId && token) {
            try {
                setAttachmentsError([])
                await applyJob({
                    id: postId,
                    data: {
                        anonymousApplication: values.anonymous,
                        applicationText:
                            settings?.fields?.applicationText.visible && values.applicationText
                                ? values.applicationText
                                : null,
                        screeningQuestions,
                        anonymousReason: values.reasonText || null,
                        'g-recaptcha-response': token,
                        attachments: attachments,
                        address: getAddress(values.address),
                        dateOfBirth: formatDateOfBirth(values.dateOfBirth) || null,
                        gender: getGenderNumber(values.gender),
                        mobilePhone: getMobilePhone(values.mobilePhone),
                        linkedinUrl: values.linkedinUrl || null,
                        cv: values.cv || null,
                    },
                }).unwrap()

                close()
                localStorage.setItem('after_apply', '1')
                localStorage.setItem('after_apply_id', postId)
                localStorage.setItem('after_apply_title', decodeHtmlEntitiesRecursive(postInfo?.data.ad.JobTitle))
                localStorage.removeItem('apply_path')
                // window.dispatchEvent(new Event('ra-input-job-application-apply'))
                setTimeout(() => {
                    location.href = '/profile'
                }, 1000)
            } catch (error: CustomError | any) {
                const {
                    email,
                    mobilePhone,
                    dateOfBirth,
                    validateMobileId,
                    screeningQuestions,
                    attachments: attachmentsError,
                    cv,
                    applicationText,
                    firstName,
                    lastName,
                    anonymousReason,
                    anonymousApplication,
                    gender,
                    linkedinUrl,
                } = error.data?.data?.errors

                if (gender) {
                    setError('gender', gender[0])
                }

                if (email) {
                    setError('email', email[0])
                }
                if (mobilePhone && mobilePhone.number) {
                    setError('mobilePhone.number', mobilePhone.number[0])
                }
                if (mobilePhone && mobilePhone.prefix) {
                    setError('mobilePhone.prefix', mobilePhone.prefix[0])
                }
                if (dateOfBirth) {
                    setError('dateOfBirth.day.value', dateOfBirth[0])
                    setError('dateOfBirth.month.value', ' ')
                    setError('dateOfBirth.year.value', ' ')
                }
                if (validateMobileId) {
                    setError('mobilePhone.number', `${t('7046')}`)
                }
                if (cv) {
                    setError('cv', cv)
                }
                if (cv && cv.base64) {
                    setError('cv.base64', cv.base64[0])
                }
                if (cv && cv.ext) {
                    setError('cv.ext', cv.ext[0])
                }
                if (cv && cv.name) {
                    setError('cv.name', cv.name[0])
                }
                if (applicationText) {
                    setError('applicationText', applicationText[0])
                }
                if (firstName) {
                    setError('firstName', firstName[0])
                }
                if (lastName) {
                    setError('lastName', lastName[0])
                }
                if (anonymousReason) {
                    setError('reasonText', anonymousReason[0])
                }
                if (anonymousApplication) {
                    setError('anonymous', anonymousApplication[0])
                }
                if (linkedinUrl) {
                    setError('linkedinUrl', linkedinUrl[0])
                }
                if (attachmentsError) {
                    setError('attachments', attachmentsError[0])
                }

                if (error.data?.data?.errors?.screeningQuestions) {
                    setQuestionsError(true)
                } else if (
                    error.data?.data?.errors?.attachments &&
                    Array.isArray(error.data?.data?.errors?.attachments)
                ) {
                    setAttachmentsError(error.data.data.errors.attachments)
                } else if (
                    error.data?.data?.errors?.attachments &&
                    !Array.isArray(error.data.data.errors.attachments)
                ) {
                    const attachmentsError = error.data.data.errors.attachments
                    const errs = Array(attachments.length)
                        .fill(null)
                        .map((item, idx) => {
                            return attachmentsError[idx] ? attachmentsError[idx] : null
                        })
                    // @ts-ignore
                    setAttachmentsError(errs)
                } else {
                    console.error(error)
                }
            } finally {
                setSubmitting(false)
            }
        }
    }

    useEffect(() => {
        settings?.screeningQuestions && setScreeningQuestions(settings?.screeningQuestions)
    }, [settings])

    return (
        <Formik
            enableReinitialize={true}
            initialValues={initialValues}
            validationSchema={Yup.object().shape({
                applicationText: Yup.string().when([], {
                    is: () => settings?.fields.applicationText.mandatory,
                    // is: () => settings?.requiredFields.applicationText,
                    then: Yup.string().required(`${t('6879')}`),
                    otherwise: Yup.string().notRequired().nullable(),
                }),
                anonymous: Yup.bool(),
                reasonText: Yup.string().when(['anonymous'], {
                    is: (anonymous: boolean) => anonymous,
                    then: Yup.string().required(`${t('9542')}`),
                    otherwise: Yup.string().notRequired().nullable(),
                }),
                cv: cvSchema.when([], {
                    is: () => settings?.fields.cv.mandatory && settings?.missingProfileFields.cv,
                    then: cvSchema.required(`${t('9807', 'CV file is required')}`),
                    otherwise: cvSchema.notRequired(),
                }),
                attachments: Yup.array().when([], {
                    is: () => settings?.fields.attachments.mandatory && settings?.missingProfileFields.attachments,
                    then: Yup.array().min(1, `${t('9317', 'Attachments required')}`),
                    otherwise: Yup.array().notRequired().nullable(),
                }),
                mobilePhone: Yup.object().when([], {
                    is: () =>
                        settings?.fields.mobilePhone.mandatory &&
                        settings?.fields.mobilePhone.visible &&
                        settings?.missingProfileFields.mobilePhone,
                    then: Yup.object().shape({
                        number: Yup.string()
                            .required(`${t('6607')}`)
                            .min(5, `${t('6608')}`)
                            .max(16, `${t('3561')}`),
                        prefix: Yup.string()
                            .min(1, `${t('5699')}`)
                            .max(7, `${t('5699')}`)
                            .required(`${t('5699')}`),
                    }),
                    otherwise: Yup.object().notRequired().nullable(),
                }),
                address: Yup.object().when([], {
                    is: () =>
                        settings?.fields.address.mandatory &&
                        settings?.fields.address.visible &&
                        settings?.missingProfileFields.address,
                    then: Yup.object().shape({
                        country: Yup.string().required(`${t('6611')}`),
                        city: Yup.string().required(`${t('6612')}`),
                    }),
                    otherwise: Yup.object({}).notRequired().nullable(),
                }),
                dateOfBirth: Yup.object().when([], {
                    is: () =>
                        settings?.fields.dateOfBirth.mandatory &&
                        settings?.fields.dateOfBirth.visible &&
                        settings?.missingProfileFields.dateOfBirth,
                    then: Yup.object().shape({
                        day: Yup.object().shape({
                            value: Yup.string().required(`${t('6613')}`),
                            label: Yup.string().required(`${t('6613')}`),
                        }),
                        month: Yup.object().shape({
                            value: Yup.string().required(`${t('6614')}`),
                            label: Yup.string().required(`${t('6614')}`),
                        }),
                        year: Yup.object().shape({
                            value: Yup.string().required(`${t('6615')}`),
                            label: Yup.string().required(`${t('6615')}`),
                        }),
                    }),
                    otherwise: Yup.object().notRequired().nullable(),
                }),
                linkedinUrl: Yup.string().when([], {
                    is: () => settings?.fields?.linkedinUrl.visible && settings?.missingProfileFields.linkedinUrl,
                    then: Yup.string().when([], {
                        is: () => settings?.fields?.linkedinUrl.mandatory && settings?.missingProfileFields.linkedinUrl,
                        then: Yup.string()
                            .required(`${t('9797', 'LinkedIn link is required')}`)
                            .nullable(true)
                            .url(`${t('9798', 'LinkedIn link is invalid')}`)
                            .matches(/^(https?:\/\/)?(www\.)?(linkedin\.com\/in\/|linkedin\.com\/pub\/)/, {
                                message: `${t('9798', 'LinkedIn link is invalid')}`,
                            }),
                        otherwise: Yup.string()
                            .notRequired()
                            .nullable(true)
                            .test('is-valid-linkedin', `${t('9798', 'LinkedIn link is invalid')}`, (value) => {
                                if (!value) return true
                                const urlRegex = /^(https?:\/\/)?(www\.)?(linkedin\.com\/in\/|linkedin\.com\/pub\/)/
                                return urlRegex.test(value)
                            }),
                    }),
                    otherwise: Yup.string().notRequired().nullable(true),
                }),
                gender: Yup.string().when([], {
                    is: () => settings?.fields?.gender.mandatory && settings?.missingProfileFields.gender,
                    then: Yup.string().oneOf(['1', '2', '3', '4'], `${t('584')}`),
                    otherwise: Yup.string().notRequired().nullable(),
                }),
            })}
            onSubmit={(values, { setSubmitting, setErrors, setFieldError }) => {
                const questionsObject = {}

                screeningQuestions.map((item: any) => {
                    Object.assign(questionsObject, {
                        [item.AdPreScreeningID]: {
                            questionId: item.AdPreScreeningID,
                            answer: item.Choosed === 0,
                        },
                    })
                })

                submitHandler(values, questionsObject, setSubmitting, setFieldError)
            }}
        >
            {({
                values,
                handleSubmit,
                handleChange,
                handleBlur,
                setFieldValue,
                isSubmitting,
                errors,
                touched,
                isValid,
            }) => {
                return (
                    <Form onSubmit={handleSubmit} style={{ marginTop: '16px' }}>
                        <ReCAPTCHA
                            sitekey={RECAPTCHA}
                            ref={recaptchaRef}
                            size={'invisible'}
                            onExpired={() => {
                                recaptchaRef.current?.reset()
                            }}
                        />
                        <Flex flexDirection={'column'} gap={5}>
                            {settings?.fields.dateOfBirth.visible && settings?.missingProfileFields.dateOfBirth && (
                                <FormControl>
                                    <FormLabel>
                                        {t('131')}
                                        {settings?.fields.dateOfBirth.mandatory && '*'}
                                    </FormLabel>
                                    <InputGroup
                                        display={'flex'}
                                        justifyContent={'space-between'}
                                        gap={1}
                                        isolation={'auto'}
                                    >
                                        <FormControl
                                            isInvalid={
                                                !!errors.dateOfBirth?.day?.value && touched.dateOfBirth?.day?.value
                                            }
                                        >
                                            <DefaultSelect
                                                options={days}
                                                placeholder={`${t('753')}`}
                                                className={'w-full'}
                                                values={values.dateOfBirth.day.value}
                                                onChange={(value) => {
                                                    setFieldValue('dateOfBirth.day', value)
                                                }}
                                                height={'170px'}
                                            />
                                            <FormErrorMessage>
                                                <Text
                                                    _firstLetter={{ textTransform: 'uppercase' }}
                                                    textTransform={'lowercase'}
                                                >
                                                    {errors.dateOfBirth?.day?.value && errors.dateOfBirth.day.value}
                                                </Text>
                                            </FormErrorMessage>
                                        </FormControl>
                                        <FormControl
                                            isInvalid={
                                                !!errors.dateOfBirth?.month?.value && touched.dateOfBirth?.month?.value
                                            }
                                        >
                                            <DefaultSelect
                                                options={Months}
                                                placeholder={`${t('931')}`}
                                                className={'w-full'}
                                                values={values.dateOfBirth.month.value}
                                                onChange={(value) => setFieldValue('dateOfBirth.month', value)}
                                                height={'170px'}
                                            />
                                            <FormErrorMessage>
                                                <Text
                                                    _firstLetter={{ textTransform: 'uppercase' }}
                                                    textTransform={'lowercase'}
                                                >
                                                    {errors.dateOfBirth?.month?.value && errors.dateOfBirth.month.value}
                                                </Text>
                                            </FormErrorMessage>
                                        </FormControl>
                                        <FormControl
                                            isInvalid={
                                                !!errors.dateOfBirth?.year?.value && touched.dateOfBirth?.year?.value
                                            }
                                        >
                                            <DefaultSelect
                                                options={years}
                                                placeholder={`${t('1322')}`}
                                                className={'w-full'}
                                                values={values.dateOfBirth.year.value}
                                                onChange={(value) => setFieldValue('dateOfBirth.year', value)}
                                                height={'170px'}
                                            />
                                            <FormErrorMessage>
                                                <Text
                                                    _firstLetter={{ textTransform: 'uppercase' }}
                                                    textTransform={'lowercase'}
                                                >
                                                    {errors.dateOfBirth?.year?.value && errors.dateOfBirth.year.value}
                                                </Text>
                                            </FormErrorMessage>
                                        </FormControl>
                                    </InputGroup>
                                </FormControl>
                            )}
                            {settings?.fields.address.visible && settings?.missingProfileFields.address && (
                                <FormControl
                                    isInvalid={
                                        (!!errors.address?.country && touched.address?.country) ||
                                        (!!errors.address?.city && touched.address?.city)
                                    }
                                >
                                    <FormLabel>
                                        {t('358')}
                                        {settings?.fields.address.mandatory && '*'}
                                    </FormLabel>
                                    <SelectGoogleAutocomplete />
                                    {errors.address?.country && (
                                        <FormErrorMessage>
                                            {errors.address?.country && errors.address.country}
                                        </FormErrorMessage>
                                    )}
                                    {errors.address?.city && (
                                        <FormErrorMessage>
                                            {errors.address?.city && errors.address.city}
                                        </FormErrorMessage>
                                    )}
                                </FormControl>
                            )}
                            {settings?.fields.mobilePhone.visible && settings?.missingProfileFields.mobilePhone && (
                                <FormControl>
                                    <FormLabel>
                                        {t('7119')}
                                        {settings?.fields.mobilePhone.mandatory && '*'}
                                    </FormLabel>
                                    <Flex
                                        flexDirection={{ base: 'column', xl: 'row' }}
                                        justifyContent={'space-between'}
                                        alignItems={isMobileValid ? 'center' : 'start'}
                                        gap={{ base: 1, xl: 2 }}
                                    >
                                        <FormControl flex={1}>
                                            <InputGroup gap={1} isolation={'auto'}>
                                                <FormControl
                                                    maxW={28}
                                                    isInvalid={
                                                        !!errors.mobilePhone?.prefix && touched.mobilePhone?.prefix
                                                    }
                                                >
                                                    <FlagSelect
                                                        onChange={(value) => {
                                                            setFieldValue('mobilePhone.prefix', value)
                                                        }}
                                                        type={'mobile'}
                                                        exist={() => phoneInputRef.current?.focus()}
                                                    />
                                                    <FormErrorMessage>
                                                        {errors.mobilePhone?.prefix && errors.mobilePhone?.prefix}
                                                    </FormErrorMessage>
                                                </FormControl>
                                                <FormControl
                                                    isInvalid={
                                                        !!errors.mobilePhone?.number && touched.mobilePhone?.number
                                                    }
                                                >
                                                    <InputGroup>
                                                        <Input
                                                            w={{
                                                                base: 'full',
                                                                xl: '250px',
                                                            }}
                                                            ref={phoneInputRef}
                                                            name={'mobilePhone.number'}
                                                            value={values.mobilePhone?.number}
                                                            onChange={(e) => {
                                                                const onlyDigits = e.target.value.replace(/\D/g, '')
                                                                handleChange({
                                                                    target: {
                                                                        name: 'mobilePhone.number',
                                                                        value: onlyDigits,
                                                                    },
                                                                })
                                                            }}
                                                            onBlur={handleBlur}
                                                            placeholder={isDesktop ? `${t('3317')}` : `${t('3306')}`}
                                                        />
                                                        <Show below={'xl'}>
                                                            {isMobileValid && (
                                                                <InputRightElement>
                                                                    <Flex
                                                                        justifyContent={'center'}
                                                                        alignItems={'center'}
                                                                    >
                                                                        <Image src={checked} />
                                                                    </Flex>
                                                                </InputRightElement>
                                                            )}
                                                        </Show>
                                                    </InputGroup>

                                                    <FormErrorMessage>
                                                        {errors.mobilePhone?.number && errors.mobilePhone?.number}
                                                    </FormErrorMessage>
                                                </FormControl>
                                            </InputGroup>
                                        </FormControl>
                                        {settings?.settings.validateMobile ? (
                                            <Box flex={1} mt={{ base: 2, xl: 0 }} w={{ base: 'full', xl: 'auto' }}>
                                                {!isMobileValid ? (
                                                    <AppButton
                                                        translate="no"
                                                        title={t('6646')}
                                                        variant={'primary'}
                                                        w={{
                                                            base: 'full',
                                                            xl: '120px',
                                                        }}
                                                        onClick={() =>
                                                            sendValidationSMS(
                                                                values.mobilePhone.prefix,
                                                                values.mobilePhone.number
                                                            )
                                                        }
                                                    />
                                                ) : (
                                                    <Show above={'xl'}>
                                                        <Flex gap={2} alignItems={'center'}>
                                                            <Image src={checked} />
                                                            <Text fontSize={'sm'} color={'state.success'}>
                                                                {t('1607')}
                                                            </Text>
                                                        </Flex>
                                                    </Show>
                                                )}
                                            </Box>
                                        ) : (
                                            <Box flex={1}></Box>
                                        )}
                                    </Flex>
                                </FormControl>
                            )}
                            {settings?.fields.gender.visible && settings?.missingProfileFields.gender && (
                                <FormControl mb={4} isInvalid={!!errors.gender && touched.gender}>
                                    <FormLabel>
                                        <Text>
                                            {t('66')}
                                            {settings?.fields.gender.mandatory && '*'}
                                        </Text>
                                    </FormLabel>
                                    <DefaultSelect
                                        placeholder={t('8887') as string}
                                        className={'w-full'}
                                        removeRightPadding={true}
                                        options={genders}
                                        onChange={(value: any) => setFieldValue('gender', value.value)}
                                        values={`${values.gender}`}
                                    />
                                    <FormErrorMessage>
                                        {errors.gender && touched.gender && errors.gender}
                                    </FormErrorMessage>
                                </FormControl>
                            )}
                            {settings?.fields.linkedinUrl.visible && settings?.missingProfileFields.linkedinUrl && (
                                <FormControl mb={4} isInvalid={!!errors.linkedinUrl && touched.linkedinUrl}>
                                    <FormLabel>
                                        {t('9691', 'LinkedIn profile link')}
                                        {settings?.fields.linkedinUrl.mandatory && '*'}
                                    </FormLabel>
                                    <Input
                                        type={'text'}
                                        name={'linkedinUrl'}
                                        placeholder={'https://www.linkedin.com/in/username'}
                                        fontSize={'sm'}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                    />
                                    <FormErrorMessage>
                                        {errors.linkedinUrl && touched.linkedinUrl && errors.linkedinUrl}
                                    </FormErrorMessage>
                                </FormControl>
                            )}
                            {settings?.fields?.applicationText.visible && (
                                <>
                                    <FormControl isInvalid={!!errors.applicationText && touched.applicationText}>
                                        <FormLabel>
                                            <HStack>
                                                <Text fontSize={'sm'} fontWeight={'medium'} color={'text.main'}>
                                                    {t('3398')}
                                                    {settings?.fields?.applicationText.mandatory && '*'}
                                                </Text>
                                                <Text fontSize={'sm'} color={'text.thirdly'}>
                                                    ({t('5339')} 1500) - {1500 - symbolsCount}
                                                </Text>
                                            </HStack>
                                        </FormLabel>
                                        <AutoResizeTextarea
                                            size={'lg'}
                                            placeholder={`${t('7124', 'Enter a short application text (max 1500 characters)')}`}
                                            fontSize={'sm'}
                                            onChange={(e: any) => {
                                                setSymbolsCount(e.target.value?.length)
                                                setFieldValue('applicationText', e.target.value)
                                            }}
                                            name={'applicationText'}
                                            value={values.applicationText}
                                            maxLength={1500}
                                        />
                                        <FormErrorMessage>
                                            {errors.applicationText &&
                                                touched.applicationText &&
                                                errors.applicationText}
                                        </FormErrorMessage>
                                    </FormControl>
                                </>
                            )}
                            {settings?.fields?.cv.visible && settings?.missingProfileFields.cv && (
                                <FormControl isInvalid={!!errors.cv}>
                                    <ApplyJobDropzone
                                        onUpload={(value) => {
                                            setFieldValue('cv', value[0])
                                            if (settings.cvParserSettings.isAvailable) {
                                                localStorage.setItem('shouldParseCV', 'true')
                                            }
                                        }}
                                        onUploadCv={(value) => {
                                            const { base64, ext, name } = value[0]
                                            setFieldValue('cv.base64', base64)
                                            setFieldValue('cv.ext', ext)
                                            setFieldValue('cv.name', name)
                                        }}
                                        label={`${t('4836', 'Upload CV')} ${settings?.fields.cv.mandatory ? '*' : ''}`}
                                        description={t('7120', 'Drop CV file here or click to upload')}
                                        accept={{
                                            'application/pdf': [],
                                            'application/msword': [],
                                            'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
                                                [],
                                        }}
                                        maxFiles={1}
                                        maxSize={10485760}
                                        height={'88px'}
                                        applyFiles={values.cv}
                                        type={'cv'}
                                        onApplyFilesDelete={() => setFieldValue('cv', null)}
                                        // errorString={
                                        //     // settings?.settings.cvRequired &&
                                        //     // settings?.requiredFields.cv &&
                                        //     settings?.fields.cv.mandatory && touched.cv && typeof errors.cv === 'string'
                                        //         ? errors.cv
                                        //         : undefined
                                        // }
                                        // errorString={errors.cv}
                                    />
                                    <FormErrorMessage>
                                        {typeof errors.cv === 'string' ? errors.cv : null}
                                    </FormErrorMessage>

                                    {values.cv && settings.cvParserSettings.isAvailable && (
                                        <Box px={'16px'} mt={4}>
                                            <Text fontSize="14px" fontWeight="bold">
                                                {t('8799')}
                                            </Text>
                                            <Text fontSize="12px" pt={'8px'}>
                                                {t('8800')}
                                            </Text>
                                            <RadioGroup
                                                name="parseCV"
                                                pt={'12px'}
                                                onChange={(value) => {
                                                    setSelectedParseValue(value)
                                                    if (value === 'no') {
                                                        localStorage.setItem('shouldParseCV', 'false')
                                                    } else {
                                                        localStorage.setItem('shouldParseCV', 'true')
                                                    }
                                                }}
                                                value={selectedParseValue}
                                            >
                                                <Stack direction="row" spacing={4}>
                                                    <Radio value="yes">
                                                        <Text fontSize="12px">{t('128')}</Text>
                                                    </Radio>
                                                    <Radio value="no">
                                                        <Text fontSize="12px">{t('8801')}</Text>
                                                    </Radio>
                                                </Stack>
                                            </RadioGroup>
                                        </Box>
                                    )}
                                </FormControl>
                            )}
                            {settings?.fields?.attachments.visible && settings?.missingProfileFields.attachments && (
                                <FormControl isInvalid={!!errors.attachments}>
                                    <ApplyJobDropzone
                                        description={t('5385')}
                                        label={`${t('7121', 'Upload attachments / application letter')} ${settings?.fields.attachments.mandatory ? '*' : ''}`}
                                        maxFiles={5}
                                        accept={{
                                            'application/msword': ['.doc', '.dotx', '.docm'],
                                            'application/vnd.openxmlformats-officedocument.wordprocessingml.document': [
                                                '.docx',
                                            ],
                                            'application/pdf': ['.pdf'],
                                            'image/gif': ['.gif'],
                                            'image/tiff': ['.tif', '.tiff'],
                                            'image/jpeg': ['.jpg', '.jpeg'],
                                            'image/png': ['.png'],
                                            'image/svg+xml': ['.svg'],
                                            'image/vnd.adobe.photoshop': ['.psd'],
                                            'application/vnd.ms-excel': ['.xls'],
                                            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': [
                                                '.xlsx',
                                                '.xlsm',
                                                '.xltx',
                                            ],
                                            'application/vnd.apple.numbers': ['.numbers'],
                                            'application/vnd.ms-powerpoint': ['.ppt', '.pps', '.pptm'],
                                            'application/vnd.openxmlformats-officedocument.presentationml.presentation':
                                                ['.pptx', '.potx'],
                                            'application/vnd.apple.keynote': ['.key'],
                                            'application/vnd.oasis.opendocument.text': ['.odt'],
                                            'application/zip': ['.zip', '.rar'],
                                            'application/json': ['.json'],
                                            'text/plain': ['.txt', '.csv'],
                                            'text/rtf': ['.rtf'],
                                            'application/vnd.ms-outlook': ['.msg', '.eml'],
                                            'application/octet-stream': ['.dat', '.nda', '.mov', '.mp4'],
                                            'application/vnd.apple.pages': ['.pages'],
                                            'application/postscript': ['.eps', '.ai'],
                                        }}
                                        type={'attachments'}
                                        onUpload={(uploadedFiles) => {
                                            setAttachmentsError([])
                                            const formattedFiles = (uploadedFiles || []).map((file) => ({
                                                base64: file.base64,
                                                ext: file.ext,
                                                name: file.name,
                                            }))
                                            if (attachments.length + formattedFiles.length > 5) {
                                                return
                                            }
                                            const newAttachments = [...attachments, ...formattedFiles].reduce<
                                                IAttachment[]
                                            >((acc, item) => {
                                                if (!acc.some((el: IAttachment) => el.name === item.name)) {
                                                    acc.push(item)
                                                }
                                                return acc
                                            }, [])
                                            setFieldValue('attachments', newAttachments)
                                            setAttachments(newAttachments)
                                        }}
                                        onUploadAttachments={(files) => {
                                            setAttachmentsError([])
                                            const formattedFiles = files.map((file) => ({
                                                base64: file.base64,
                                                ext: file.ext,
                                                name: file.name,
                                            }))
                                            const newAttachments = [...attachments, ...formattedFiles].reduce<
                                                IAttachment[]
                                            >((acc, item) => {
                                                if (!acc.some((el: IAttachment) => el.name === item.name)) {
                                                    acc.push(item)
                                                }
                                                return acc
                                            }, [])
                                            setFieldValue('attachments', newAttachments)
                                            setAttachments(attachments)
                                        }}
                                        applyFiles={attachments}
                                        onApplyFilesDelete={(file) => {
                                            // setAttachmentsError([])
                                            // setAttachments((prevAttachments) =>
                                            //     prevAttachments.filter((item) => item.name !== file.name)
                                            // )
                                            const updatedAttachments = values.attachments.filter(
                                                (f) => f.name !== file.name
                                            )
                                            setAttachments(updatedAttachments)
                                            setFieldValue('attachments', updatedAttachments)
                                        }}
                                        isAlternative={true}
                                        attachmentErrors={attachmentsError}
                                    />
                                    <FormErrorMessage className={'Adasd'}>
                                        {typeof errors.attachments === 'string'
                                            ? errors.attachments
                                            : Array.isArray(errors.attachments)
                                              ? errors.attachments[0] && typeof errors.attachments[0] === 'string'
                                                  ? errors.attachments[0]
                                                  : null
                                              : null}
                                    </FormErrorMessage>
                                </FormControl>
                            )}
                            <FormControl isInvalid={questionsError}>
                                {isSettingsLoading ? (
                                    <Skeleton w={'full'} h={'52px'} />
                                ) : (
                                    screeningQuestions?.length > 0 && (
                                        <VStack gap={2} w={'full'} alignItems={'start'}>
                                            {screeningQuestions.map((question) => (
                                                <Box
                                                    key={question.AdPreScreeningID}
                                                    display={'flex'}
                                                    flexDirection={'column'}
                                                    gap={3}
                                                    w={'full'}
                                                >
                                                    <Text fontSize={'sm'} fontWeight={'semibold'} color={'text.main'}>
                                                        {question.Question}
                                                    </Text>
                                                    <RadioGroup
                                                        onChange={(value) => {
                                                            setQuestionsError(false)
                                                            let questionObject = screeningQuestions.find(
                                                                (object) =>
                                                                    object.AdPreScreeningID ===
                                                                    question.AdPreScreeningID
                                                            )
                                                            if (questionObject) {
                                                                questionObject = {
                                                                    ...questionObject,
                                                                    Choosed: +value,
                                                                }
                                                                let newQuestionArray = screeningQuestions.map((item) =>
                                                                    item.AdPreScreeningID ===
                                                                    questionObject?.AdPreScreeningID
                                                                        ? questionObject
                                                                        : item
                                                                )
                                                                setScreeningQuestions(newQuestionArray)
                                                            }
                                                        }}
                                                        // @ts-ignore
                                                        value={question.Choosed}
                                                    >
                                                        <HStack>
                                                            <Radio value={0} fontSize={'sm'}>
                                                                <Text fontSize={'sm'}>{t('128')}</Text>
                                                            </Radio>
                                                            <Radio value={1} fontSize={'sm'}>
                                                                <Text fontSize={'sm'}>{t('129')}</Text>
                                                            </Radio>
                                                        </HStack>
                                                    </RadioGroup>
                                                    {question.Answer === question.Choosed ? (
                                                        <FieldError text={question.Response} />
                                                    ) : null}

                                                    <Divider />
                                                </Box>
                                            ))}
                                        </VStack>
                                    )
                                )}
                                <FormErrorMessage>
                                    <FieldError text={`${t('7098')}`} />
                                </FormErrorMessage>
                            </FormControl>
                            {isSettingsLoading ? (
                                <Skeleton w={'full'} h={'109px'} />
                            ) : settings?.settings?.anonymousApplication ? (
                                <FormControl>
                                    <Checkbox
                                        checked={values.anonymous}
                                        onChange={(e) => setFieldValue('anonymous', e.target.checked)}
                                    >
                                        <Text fontSize={'sm'} color={'text.main'}>
                                            {t('8578')}
                                        </Text>
                                    </Checkbox>
                                </FormControl>
                            ) : null}

                            {values.anonymous && (
                                <FormControl isInvalid={!!errors.reasonText && touched.reasonText}>
                                    <FormLabel>
                                        <HStack>
                                            <Text fontSize={'sm'} fontWeight={'medium'} color={'text.main'}>
                                                {t('2371')}*
                                            </Text>
                                            {/*<Text*/}
                                            {/*    fontSize={'sm'}*/}
                                            {/*    fontWeight={'medium'}*/}
                                            {/*    color={'text.thirdly'}*/}
                                            {/*    textTransform={'lowercase'}*/}
                                            {/*>*/}
                                            {/*    ({t('6149')})*/}
                                            {/*</Text>*/}
                                        </HStack>
                                    </FormLabel>
                                    <Textarea
                                        size={'lg'}
                                        name={'reasonText'}
                                        placeholder={`${t('6636')}`}
                                        fontSize={'sm'}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                    />
                                    <FormErrorMessage className={'Errors__reasonText'}>
                                        {errors.reasonText && errors.reasonText}
                                    </FormErrorMessage>
                                </FormControl>
                            )}
                            <FormControl>
                                <AppButton
                                    translate="no"
                                    title={t('1111')}
                                    type={'submit'}
                                    variant={'primary'}
                                    id={'ra-input-job-application-apply'}
                                    isLoading={isLoading || isSubmitting}
                                    isDisabled={isLoading || isSubmitting || !isValid}
                                />
                            </FormControl>
                        </Flex>
                    </Form>
                )
            }}
        </Formik>
    )
}
